/**
 * DỊCH MESSAGE TỪ TẦNG `sim/` — cầu nối để toast/ bảng không lọt tiếng Việt khi chơi tiếng Anh.
 *
 * ⚠️ VÌ SAO KHÔNG SỬA THẲNG `sim/`: message trong `sim/` là **DỮ LIỆU** (hàm thuần, có unit test
 * khoá nội dung, xem `tests/craft.test.ts`). Nhét i18n vào đó sẽ: (a) làm hàm thuần phụ thuộc
 * trạng thái toàn cục, (b) phá test hiện có, (c) buộc mọi test phải set ngôn ngữ trước.
 *
 * Cách làm ở đây: `sim()` là bảng MẪU (regex + hàm dựng câu). Tiếng Việt không khớp mẫu nào thì
 * trả về NGUYÊN VĂN — thêm message mới trong `sim/` sẽ chỉ hiện tiếng Việt (không ném, không vỡ),
 * và test `tests/i18n.test.ts` quét toàn bộ message hiện có để bắt chỗ chưa phủ.
 */
import { CLASSES, type ClassId } from '../data/classes';
import { EXTRA_SKILLS } from '../data/skills-extra';
import { className, potionName, potionShortName, skillName } from './loc';
import { getLang } from './index';

/** Bảng mẫu: khớp message tiếng Việt → dựng câu tiếng Anh từ các nhóm bắt được. */
type Rule = { re: RegExp; en: (m: RegExpMatchArray) => string };

const RULES: Rule[] = [
  // ---- vàng/ giá
  { re: /^Không đủ vàng \(cần ([\d.,]+)\)$/, en: (m) => `Not enough gold (need ${m[1]})` },
  // ---- sổ nhân vật
  { re: /^Sổ nhân vật đã đủ (\d+) — mỗi trận chỉ ra sân \1 nhân vật$/, en: (m) => `Roster is full (${m[1]}) — only ${m[1]} heroes can be deployed per battle` },
  { re: /^Sổ nhân vật đã đủ (\d+) — không ghép thêm được$/, en: (m) => `Roster is full (${m[1]}) — cannot craft more` },
  { re: /^(.+) ra sân$/, en: (m) => `${deployEN(m[1])} deployed` },
  // ---- tập luyện
  { re: /^(.+) lên cấp tập (\d+)$/, en: (m) => `${m[1]} reached training level ${m[2]}` },
  { re: /^Mất tướng$/, en: () => 'Hero lost' },
  { re: /^Hết vàng — dừng tập luyện$/, en: () => 'Out of gold — training stopped' },
  { re: /^Đã dừng$/, en: () => 'Stopped' },
  // ---- bình thuốc/ đạn
  { re: /^Mua (\d+) (.+)$/, en: (m) => `Bought ${m[1]} × ${potionEN(m[2])}` },
  // ---- ghép mảnh
  { re: /^Không có class: (.+)$/, en: (m) => `Unknown class: ${classEN(m[1])}` },
  /*
   * TÚI ĐỒ — mở khoá / nâng cấp / bán MẢNH CLASS (v7).
   * ⚠️ `Thiếu mảnh {class}: cần N, đang có M` dùng cho CẢ mở khoá (30) lẫn nâng cấp (10) — một mẫu
   * là đủ vì số được bắt lại. Phải đặt TRƯỚC các mẫu `Thiếu mảnh tướng …`/ `Thiếu mảnh chiêu …`
   * (khác từ khoá "tướng"/"chiêu") và trước mọi mẫu `(.+)` chung.
   */
  { re: /^Bán (\d+) mảnh được (\d+) vàng$/, en: (m) => `Sold ${m[1]} shards for ${m[2]} gold` },
  { re: /^(.+) \+1 sức mạnh \(tổng (\d+)\)$/, en: (m) => `${classEN(m[1])} +1 power (total ${m[2]})` },
  { re: /^Đã mở khoá (.+)$/, en: (m) => `Unlocked ${classEN(m[1])}` },
  { re: /^(.+) đã mở khoá rồi$/, en: (m) => `${classEN(m[1])} is already unlocked` },
  { re: /^Chưa mở khoá (.+)$/, en: (m) => `${classEN(m[1])} is still locked` },
  { re: /^Chưa mở khoá (.+) — cần (\d+) mảnh \(đang có (\d+)\)$/, en: (m) => `${classEN(m[1])} is locked — needs ${m[2]} shards (have ${m[3]})` },
  { re: /^Số mảnh phải lớn hơn 0$/, en: () => 'Shard count must be greater than 0' },
  { re: /^Thiếu mảnh tướng (.+): cần (\d+), đang có (\d+)$/, en: (m) => `Not enough hero shards for ${classEN(m[1])}: need ${m[2]}, have ${m[3]}` },
  { re: /^Thiếu mảnh chiêu (.+): cần (\d+), đang có (\d+)$/, en: (m) => `Not enough skill shards for ${classEN(m[1])}: need ${m[2]}, have ${m[3]}` },
  /*
   * ⚠️⚠️ THỨ TỰ QUYẾT ĐỊNH — BẪY ĐÃ DÍNH LẦN THỨ BA TRONG FILE NÀY.
   * `Thiếu mảnh (.+): cần N` là mẫu TỔNG QUÁT, phải nằm SAU `Thiếu mảnh tướng …` và
   * `Thiếu mảnh chiêu …`. Bản đầu tôi đặt nó TRƯỚC ⇒ ăn mất `Thiếu mảnh chiêu Dân tập gym: cần 25`
   * và dịch thành "Not enough Dân tập gym shards" (sai + còn dấu tiếng Việt) — test i18n bắt được.
   */
  { re: /^Thiếu mảnh (.+): cần (\d+), đang có (\d+)$/, en: (m) => `Not enough ${classEN(m[1])} shards: need ${m[2]}, have ${m[3]}` },
  { re: /^Không đủ mảnh (.+) \(đang có (\d+)\)$/, en: (m) => `Not enough ${classEN(m[1])} shards (have ${m[2]})` },
  /*
   * ⚠️ THỨ TỰ QUAN TRỌNG: mẫu CỤ THỂ phải đứng TRƯỚC mẫu TỔNG QUÁT, vì `(.+)` rất "tham" —
   * `^Ghép được (.+)$` sẽ ăn luôn `"Ghép được X cho Y"` và dịch sai (đã dính đúng lỗi này).
   */
  { re: /^Ghép được (.+) cho (.+)$/, en: (m) => `Crafted ${skillEN(m[1])} for ${classEN(m[2])}` },
  { re: /^Ghép được (.+)$/, en: (m) => `Crafted ${classEN(m[1])}` },
  { re: /^(.+) là chiêu gốc — đã có sẵn, không cần ghép$/, en: (m) => `${skillEN(m[1])} is a base move — already owned, no crafting needed` },
  { re: /^Không có chiêu (.+) cho (.+)$/, en: (m) => `No move ${skillEN(m[1])} for ${classEN(m[2])}` },
  { re: /^Đã ghép (.+) rồi$/, en: (m) => `${skillEN(m[1])} is already crafted` },
  // ---- xếp ô chiêu
  { re: /^Ô (\d+) không hợp lệ \(chỉ có ô 0\.\.(\d+)\)$/, en: (m) => `Slot ${m[1]} is invalid (only slots 0..${m[2]})` },
  { re: /^Ô (\d+) không có chiêu nào$/, en: (m) => `Slot ${m[1]} is empty` },
  { re: /^Chiêu (.+) không thuộc (.+)$/, en: (m) => `Move ${skillEN(m[1])} does not belong to ${classEN(m[2])}` },
  { re: /^Chưa sở hữu (.+) — phải ghép mảnh trước$/, en: (m) => `${skillEN(m[1])} not owned yet — craft its shards first` },
  { re: /^(.+) đã nằm ở ô (\d+)$/, en: (m) => `${skillEN(m[1])} is already in slot ${m[2]}` },
  { re: /^Không thể bỏ chiêu gốc cuối cùng của class$/, en: () => 'Cannot remove the last base move of a class' },
  { re: /^(.+) trang bị (.+) vào ô (\d+)$/, en: (m) => `${classEN(m[1])} equipped ${skillEN(m[2])} in slot ${m[3]}` },
  { re: /^(.+) bỏ (.+) khỏi ô (\d+)$/, en: (m) => `${classEN(m[1])} removed ${skillEN(m[2])} from slot ${m[3]}` },
  // ---- shop kim cương
  { re: /^Không đủ kim cương \(cần ([\d.,]+)\)$/, en: (m) => `Not enough diamonds (need ${m[1]})` },
  { re: /^Mua (.+) — còn ([\d.,]+) kim cương$/, en: (m) => `Bought ${m[1]} — ${m[2]} diamonds left` },
  // ---- BỔ SUNG: quét hết message của `sim/` (xem tests/i18n.test.ts)
  { re: /^Bắt đầu tập$/, en: () => 'Training started' },
  { re: /^Cần ([\d.,]+) vàng$/, en: (m) => `Need ${m[1]} gold` },
  { re: /^Chiêu mộ (.+) — còn ([\d.,]+)💎$/, en: (m) => `Recruited ${classEN(m[1])} — ${m[2]}💎 left` },
  { re: /^Chưa chọn tướng$/, en: () => 'No hero selected' },
  { re: /^Class lạ: (.+)$/, en: (m) => `Unknown class: ${classEN(m[1])}` },
  { re: /^Đã có (.+) rồi$/, en: (m) => `Already own ${skillEN(m[1])}` },
  { re: /^Đã dùng tối đa bình thể lực trong màn này$/, en: () => 'Used the max stamina potions this stage' },
  { re: /^Dùng (.+)$/, en: (m) => `Used ${classEN(m[1])}` },
  { re: /^Không có chiêu: (.+)$/, en: (m) => `No such move: ${skillEN(m[1])}` },
  { re: /^Không có tướng này$/, en: () => 'No such hero' },
  { re: /^Không đủ kim cương \(cần ([\d.,]+), đang có ([\d.,]+)\)$/, en: (m) => `Not enough diamonds (need ${m[1]}, have ${m[2]})` },
  { re: /^Loại món chưa hỗ trợ: (.+)$/, en: (m) => `Unsupported item kind: ${m[1]}` },
  { re: /^Món (.+) thiếu số lượng mảnh$/, en: (m) => `Item ${m[1]} is missing a shard amount` },
  { re: /^Món (.+) thiếu target$/, en: (m) => `Item ${m[1]} is missing a target` },
  { re: /^Mua (\d+) viên đạn$/, en: (m) => `Bought ${m[1]} bullets` },
  { re: /^Mua (.+) cho (.+) — còn ([\d.,]+)💎$/, en: (m) => `Bought ${skillEN(m[1])} for ${classEN(m[2])} — ${m[3]}💎 left` },
  { re: /^Nhận (\d+) mảnh \((.+)\) — còn ([\d.,]+)💎$/, en: (m) => `Got ${m[1]} shards (${m[2]}) — ${m[3]}💎 left` },
  { re: /^Phải giữ tối thiểu 1 chiêu$/, en: () => 'Must keep at least 1 move' },
  { re: /^Shop không có món: (.+)$/, en: (m) => `Shop has no item: ${m[1]}` },
  { re: /^Sổ nhân vật đã đủ (\d+) — không chiêu mộ thêm$/, en: (m) => `Roster is full (${m[1]}) — cannot recruit more` },
  // ⚠️ HAI LÝ DO KHÁC NHAU — đừng gộp: "sân đủ" (hết chỗ đứng) KHÁC "sổ đủ" (hết bản ghi để ra sân)
  { re: /^Sân đã đủ (\d+) tướng — đợi tướng gục hoặc qua màn$/, en: (m) => `Field already has ${m[1]} heroes — wait for one to fall or clear the wave` },
  /*
   * ARENA: trần nông dân của người chơi (02/10/2026: *"để max mua được 4 farmer"*). Message do
   * `ArenaWorld.buyFarmer` sinh ra; HUD hiện nó qua toast.
   */
  { re: /^Đã đủ (\d+) nông dân$/, en: (m) => `Farmer limit reached (${m[1]})` },
  { re: /^Sổ đã đủ (\d+) nhân vật và cả (\d+) (.+) đều đã ra sân \((\d+)\/(\d+)\) — xoá bớt hoặc dùng class khác$/, en: (m) => `Roster already has ${m[1]} heroes and all ${m[2]} ${classEN(m[3])} are on the field (${m[4]}/${m[5]}) — remove one or use another class` },
  /*
   * ⚠️ MẪU TỔNG QUÁT PHẢI ĐỨNG CUỐI NHÓM. `^Hết (.+)$` (dịch "hết bình thuốc") từng nằm TRÊN
   * `^Hết nhân vật …$` nên ăn mất message mới và cho ra câu lai: *"Out of nhân vật Đẻ mướn chưa ra
   * sân (đã ra sân 0/5)"* — test i18n bắt được. Đây là lần thứ HAI dính bẫy này (lần đầu với
   * `^Ghép được (.+)$`), nên nhớ: mẫu cụ thể TRƯỚC, mẫu `(.+)` SAU.
   */
  { re: /^Hết (.+)$/, en: (m) => `Out of ${potionEN(m[1])}` },
  { re: /^Tướng này không dùng đạn$/, en: () => 'This hero does not use ammo' },
];

/**
 * Tên class trong message sim là TIẾNG VIỆT (`CLASSES[id].name`) ⇒ nếu message đã ở tiếng Anh thì
 * tra ngược về id để dịch. Không tìm thấy thì trả nguyên văn.
 */
function classEN(name: string): string {
  const id = (Object.keys(CLASSES) as ClassId[]).find((k) => CLASSES[k].name === name);
  return id ? className(id) : name;
}

/**
 * `buyHero()` trả `"<tên> ra sân"` mà tên đó là TÊN CLASS ⇒ phải tra ngược về id rồi dịch.
 * (Tách riêng cho rõ nghĩa; `classEN()` làm đúng việc này.)
 */
function deployEN(name: string): string {
  return classEN(name);
}

/** Tên chiêu trong message sim là tiếng Việt ⇒ tra ngược rồi dịch theo ngôn ngữ đang chọn. */
function skillEN(name: string): string {
  /*
   * ⚠️ Tra theo NAME (message của sim in tên chiêu, không in id) rồi mới gọi `skillName()` — hàm
   * đó tra bản dịch theo **id**. Tra thẳng `SKILL_EN[name]` sẽ luôn trượt.
   */
  for (const cls of Object.values(CLASSES)) {
    const s = cls.skills.find((x) => x.name === name);
    if (s) return skillName(s);
  }
  // chiêu GHÉP (15 chiêu trong `data/skills-extra.ts`) không nằm trong `CLASSES.skills`
  for (const list of Object.values(EXTRA_SKILLS)) {
    const s = list.find((x) => x.name === name);
    if (s) return skillName(s);
  }
  return name;
}

function potionEN(name: string): string {
  if (name.includes('Máu')) return potionShortName('hp');
  if (name.includes('Mana')) return potionShortName('mana');
  if (name.includes('Thể Lực') || name.includes('Thể lực')) return potionShortName('stamina');
  return name;
}

/**
 * Dịch 1 message của `sim/`. Ở chế độ tiếng Việt (hoặc khi không khớp mẫu nào) trả NGUYÊN VĂN.
 */
export function simMsg(message: string): string {
  if (!message || getLang() !== 'en') return message;
  for (const rule of RULES) {
    const m = message.match(rule.re);
    if (m) return rule.en(m);
  }
  return message;
}

/** Chỉ để test: đếm số mẫu đã khai (test quét phủ message của sim). */
export const SIM_MSG_RULE_COUNT = RULES.length;

/** Chỉ để test: tên bình thuốc tiếng Anh (dùng `potionName` để giữ 1 nguồn). */
export const potionNameEN = potionName;
